import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useLanguage } from '../../context/LanguageContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card'
import {
  Gear,
  User,
  Buildings,
  Bell,
  Lock,
  Globe,
  CreditCard,
  ShieldCheck,
  EnvelopeSimple,
  Phone,
  MapPin,
  FileText,
  Check,
  CircleNotch,
  CurrencyDollar,
  ArrowsClockwise,
  Camera,
  Trash,
  Lightning,
  PencilSimple,
  UsersThree,
} from '@phosphor-icons/react'
import { useSavedTemplates, SavedTemplate } from '../../hooks/useSavedTemplates'
import { useOfferConditions, OfferCondition } from '../../hooks/useOfferConditions'
import { EditTemplateModal } from '../../components/offer/EditTemplateModal'
import { BreakdownItem } from '../../components/offer/FeeBreakdown'
import { changePassword, uploadAvatar, deleteAvatar, uploadCompanyLogo, deleteCompanyLogo, updateProfile } from '../../services/auth'
import { api } from '../../services/api'
import { useDialog } from '../../context/DialogContext'
import { TeamTab } from '../../components/settings/TeamTab'
import { ImageCropperModal } from '../../components/ImageCropperModal'

export default function SettingsPage() {
  const { user, refreshProfile } = useAuth()
  const { t } = useLanguage()
  const { showSuccess, toastSuccess, toastError } = useDialog()

  // TODO: Implement settings state management
  const [activeTab, setActiveTab] = useState<'profile' | 'company' | 'team' | 'notifications' | 'security' | 'billing' | 'templates' | 'conditions'>('profile')
  const [hasChanges, setHasChanges] = useState(false)

  // User profile data (from real API)
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    cargo: user?.cargo || '',
    department: user?.department || '',
  })

  const [companyData, setCompanyData] = useState({
    name: user?.current_company?.name || 'Empresa Exemplo Ltda',
    document: '12.345.678/0001-90',
    phone: '+55 11 3456-7890',
    email: 'contato@empresa.com',
    website: 'www.empresa.com',
    address: {
      street: 'Rua Exemplo, 123',
      complement: 'Sala 45',
      neighborhood: 'Centro',
      city: 'São Paulo',
      state: 'SP',
      zipCode: '01310-100',
      country: 'Brasil',
    },
    specializations: ['Marítimo', 'Aéreo', 'Importação'],
    languages: ['Português', 'Inglês', 'Espanhol'],
  })

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: {
      newOpportunities: true,
      proposalUpdates: true,
      shipmentAlerts: true,
      clientMessages: true,
      weeklyReport: true,
    },
    pushNotifications: {
      urgentOpportunities: true,
      acceptedProposals: true,
      shipmentMilestones: true,
    },
  })

  const [securitySettings, setSecuritySettings] = useState({
    twoFactorEnabled: false,
    sessionTimeout: '30',
    loginAlerts: true,
  })

  // Exchange rate settings
  const [exchangeRateType, setExchangeRateType] = useState<'comercial' | 'ptax'>('comercial')
  const [exchangeRateLoading, setExchangeRateLoading] = useState(false)
  // Agent fixed spread (Plane #25)
  const [spread, setSpread] = useState<string>('')
  const [savingSpread, setSavingSpread] = useState(false)
  const [currentRate, setCurrentRate] = useState<{ buy: number; sell: number; date: string } | null>(null)
  const [rateLoading, setRateLoading] = useState(false)

  // Currency preference
  const [preferredCurrency, setPreferredCurrency] = useState<'BRL' | 'USD'>('BRL')
  const [currencyLoading, setCurrencyLoading] = useState(false)

  // Templates management
  const { templates: allTemplates, deleteTemplate, updateTemplate, refresh: refreshTemplates } = useSavedTemplates()
  const [editingTemplate, setEditingTemplate] = useState<SavedTemplate | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  // Conditions management
  const { conditions: allConditions, saveCondition, updateCondition, deleteCondition, toggleDefault, refresh: refreshConditions } = useOfferConditions()
  const [editingCondition, setEditingCondition] = useState<OfferCondition | null>(null)
  const [isConditionModalOpen, setIsConditionModalOpen] = useState(false)
  const [newCondition, setNewCondition] = useState({ title: '', content: '', isDefault: false })

  // Handle template update from modal
  const handleTemplateUpdate = (id: string, name: string, breakdown: BreakdownItem[]) => {
    updateTemplate(id, name, breakdown)
    refreshTemplates()
    setIsEditModalOpen(false)
    setEditingTemplate(null)
  }

  // Load settings on mount
  useEffect(() => {
    loadSettings()
  }, [])

  // Load exchange rate when type changes
  useEffect(() => {
    if (exchangeRateType) {
      fetchCurrentRate()
    }
  }, [exchangeRateType])

  async function loadSettings() {
    try {
      const response = await api.get('/freight-forwarder/settings')
      const settings = response.data.settings
      if (settings?.settings?.exchangeRateType) {
        setExchangeRateType(settings.settings.exchangeRateType)
      }
      if (settings?.settings?.preferredCurrency) {
        setPreferredCurrency(settings.settings.preferredCurrency)
      }
      if (settings?.spreadPercentage != null) {
        setSpread(String(settings.spreadPercentage))
      }
    } catch (err) {
      console.error('Failed to load settings:', err)
    }
  }

  async function handleSaveSpread() {
    setSavingSpread(true)
    try {
      await api.put('/freight-forwarder/settings', {
        spreadPercentage: spread === '' ? null : Number(spread),
      })
      toastSuccess('Spread salvo', 'Seu spread foi atualizado.')
    } catch (err: any) {
      toastError('Erro', err.response?.data?.error || 'Erro ao salvar spread')
    } finally {
      setSavingSpread(false)
    }
  }

  async function fetchCurrentRate() {
    setRateLoading(true)
    try {
      const response = await api.get(`/exchange-rates/current?type=${exchangeRateType}`)
      const rate = response.data.rate
      setCurrentRate({
        buy: rate.buy,
        sell: rate.sell,
        date: rate.date
      })
    } catch (err) {
      console.error('Failed to fetch exchange rate:', err)
    } finally {
      setRateLoading(false)
    }
  }

  async function handleSaveExchangeRateType(newType: 'comercial' | 'ptax') {
    setExchangeRateLoading(true)
    try {
      await api.patch('/freight-forwarder/settings', {
        settings: { exchangeRateType: newType }
      })
      setExchangeRateType(newType)
    } catch (err) {
      console.error('Failed to save exchange rate type:', err)
    } finally {
      setExchangeRateLoading(false)
    }
  }

  async function handleSavePreferredCurrency(newCurrency: 'BRL' | 'USD') {
    setCurrencyLoading(true)
    try {
      await api.patch('/freight-forwarder/settings', {
        settings: { preferredCurrency: newCurrency }
      })
      setPreferredCurrency(newCurrency)
      toastSuccess('Moeda Padrão', `Moeda padrão alterada para ${newCurrency === 'BRL' ? 'Real (R$)' : 'Dólar (US$)'}`)
    } catch (err) {
      console.error('Failed to save preferred currency:', err)
    } finally {
      setCurrencyLoading(false)
    }
  }

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')

  // Avatar state
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(user?.avatar_url || null)
  const [avatarCropFile, setAvatarCropFile] = useState<File | null>(null)
  const [logoCropFile, setLogoCropFile] = useState<File | null>(null)
  const [avatarLoading, setAvatarLoading] = useState(false)
  const [avatarError, setAvatarError] = useState('')

  // Company logo state
  const logoInputRef = useRef<HTMLInputElement>(null)
  const [logoUrl, setLogoUrl] = useState<string | null>(user?.current_company?.logo_url || null)
  const [logoLoading, setLogoLoading] = useState(false)
  const [logoError, setLogoError] = useState('')

  // Sync avatar URL when user data changes
  useEffect(() => {
    if (user?.avatar_url) {
      setAvatarUrl(user.avatar_url)
    }
  }, [user?.avatar_url])

  // Sync logo URL when user data changes
  useEffect(() => {
    if (user?.current_company?.logo_url) {
      setLogoUrl(user.current_company.logo_url)
    }
  }, [user?.current_company?.logo_url])

  const handleAvatarUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      setAvatarError('Tipo de arquivo inválido. Apenas JPEG, PNG, GIF e WebP são permitidos.')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setAvatarError('Arquivo muito grande. Tamanho máximo é 5MB.')
      return
    }

    setAvatarError('')
    setAvatarCropFile(file)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleAvatarCropConfirm = async (croppedFile: File) => {
    setAvatarCropFile(null)
    setAvatarLoading(true)
    setAvatarError('')
    try {
      const result = await uploadAvatar(croppedFile)
      setAvatarUrl(result.avatar_url)
      toastSuccess('Foto Atualizada', 'Sua foto de perfil foi salva com sucesso.')
    } catch (err: any) {
      setAvatarError(err.response?.data?.error || 'Erro ao fazer upload do avatar')
    } finally {
      setAvatarLoading(false)
    }
  }

  const handleAvatarDelete = async () => {
    if (!avatarUrl) return

    setAvatarLoading(true)
    setAvatarError('')

    try {
      await deleteAvatar()
      setAvatarUrl(null)
      toastSuccess('Foto Removida', 'Sua foto de perfil foi removida com sucesso.')
    } catch (err: any) {
      setAvatarError(err.response?.data?.error || 'Erro ao excluir avatar')
    } finally {
      setAvatarLoading(false)
    }
  }

  // Logo upload handlers
  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']
    if (!allowedTypes.includes(file.type)) {
      setLogoError('Tipo de arquivo inválido. Apenas JPEG, PNG, GIF, WebP e SVG são permitidos.')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setLogoError('Arquivo muito grande. Tamanho máximo é 5MB.')
      return
    }

    setLogoError('')
    if (file.type === 'image/svg+xml') {
      void uploadLogoFile(file)
      if (logoInputRef.current) logoInputRef.current.value = ''
      return
    }
    setLogoCropFile(file)
    if (logoInputRef.current) logoInputRef.current.value = ''
  }

  const uploadLogoFile = async (file: File) => {
    setLogoLoading(true)
    setLogoError('')
    try {
      const result = await uploadCompanyLogo(file)
      setLogoUrl(result.logoUrl)
      toastSuccess('Logo Atualizado', 'O logo da empresa foi salvo com sucesso.')
      await refreshProfile()
    } catch (err: any) {
      setLogoError(err.response?.data?.error || 'Erro ao fazer upload do logo')
    } finally {
      setLogoLoading(false)
    }
  }

  const handleLogoDelete = async () => {
    if (!logoUrl) return

    setLogoLoading(true)
    setLogoError('')

    try {
      await deleteCompanyLogo()
      setLogoUrl(null)
      toastSuccess('Logo Removido', 'O logo da empresa foi removido com sucesso.')
      // Refresh profile to update global state
      await refreshProfile()
    } catch (err: any) {
      setLogoError(err.response?.data?.error || 'Erro ao excluir logo')
    } finally {
      setLogoLoading(false)
    }
  }

  // Get user initials for avatar placeholder
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase()
  }

  // Profile save handler
  const [isSavingProfile, setIsSavingProfile] = useState(false)
  const handleSaveProfile = async () => {
    setIsSavingProfile(true)
    try {
      await updateProfile({
        name: profileData.name,
        phone: profileData.phone,
        cargo: profileData.cargo,
        department: profileData.department,
      })
      await refreshProfile()
      setHasChanges(false)
      showSuccess('Perfil Atualizado', 'Suas informações foram salvas com sucesso.')
    } catch (err: any) {
      console.error('Error saving profile:', err)
      showSuccess('Erro', err.response?.data?.error || 'Erro ao salvar perfil')
    } finally {
      setIsSavingProfile(false)
    }
  }

  const handleSaveCompany = () => {
    // TODO: API call to update company
    console.log('Saving company:', companyData)
    setHasChanges(false)
    showSuccess('Empresa Atualizada', 'Os dados da empresa foram salvos com sucesso.')
  }

  const handleSaveNotifications = () => {
    // TODO: API call to update notifications
    console.log('Saving notifications:', notificationSettings)
    setHasChanges(false)
    showSuccess('Notificações', 'Suas preferências de notificação foram atualizadas.')
  }

  const handleSaveSecurity = () => {
    // TODO: API call to update security settings
    console.log('Saving security:', securitySettings)
    setHasChanges(false)
    showSuccess('Segurança', 'Suas configurações de segurança foram atualizadas.')
  }

  const handleChangePassword = async () => {
    setPasswordError('')
    setPasswordSuccess('')

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Preencha todos os campos')
      return
    }

    if (newPassword.length < 6) {
      setPasswordError('A nova senha deve ter pelo menos 6 caracteres')
      return
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('As senhas não conferem')
      return
    }

    setPasswordLoading(true)

    try {
      await changePassword(currentPassword, newPassword)
      setPasswordSuccess('Senha alterada com sucesso!')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err: any) {
      setPasswordError(err.response?.data?.error || 'Erro ao alterar senha')
    } finally {
      setPasswordLoading(false)
    }
  }

  const handleEnable2FA = () => {
    // TODO: Implement 2FA setup flow
    console.log('Enable 2FA clicked')
  }

  return (
    <AppLayout>
      {avatarCropFile && (
        <ImageCropperModal
          file={avatarCropFile}
          title="Ajustar foto de perfil"
          aspect={1}
          cropShape="round"
          outputSize={512}
          outputMime="image/jpeg"
          onCancel={() => setAvatarCropFile(null)}
          onConfirm={handleAvatarCropConfirm}
        />
      )}
      {logoCropFile && (
        <ImageCropperModal
          file={logoCropFile}
          title="Ajustar logo da empresa"
          aspect={1}
          cropShape="rect"
          outputSize={1024}
          outputMime="image/png"
          onCancel={() => setLogoCropFile(null)}
          onConfirm={(cropped) => { setLogoCropFile(null); void uploadLogoFile(cropped) }}
        />
      )}
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Gear size={28} />
                Configurações
              </h1>
              <p className="text-sm text-gray-500">
                Gerencie suas preferências e informações
              </p>
            </div>
            {hasChanges && (
              <div className="flex items-center gap-2 text-sm text-orange-600">
                <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
                Você tem alterações não salvas
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar Navigation */}
          <aside className="lg:w-64 flex-shrink-0">
            <nav className="space-y-1">
              <button
                onClick={() => setActiveTab('profile')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeTab === 'profile'
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <User size={20} />
                Perfil Pessoal
              </button>
              <button
                onClick={() => setActiveTab('company')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeTab === 'company'
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Buildings size={20} />
                Empresa
              </button>
              <button
                onClick={() => setActiveTab('team')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeTab === 'team'
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <UsersThree size={20} />
                Equipe
              </button>
              <button
                onClick={() => setActiveTab('notifications')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeTab === 'notifications'
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Bell size={20} />
                Notificações
              </button>
              <button
                onClick={() => setActiveTab('security')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeTab === 'security'
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Lock size={20} />
                Segurança
              </button>
              <button
                onClick={() => setActiveTab('billing')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeTab === 'billing'
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <CreditCard size={20} />
                Assinatura & Faturamento
              </button>
              <button
                onClick={() => setActiveTab('templates')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeTab === 'templates'
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Lightning size={20} />
                Templates de Ofertas
              </button>
              <button
                onClick={() => setActiveTab('conditions')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeTab === 'conditions'
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <FileText size={20} />
                Condições de Ofertas
              </button>
            </nav>
          </aside>

          {/* Content Area */}
          <div className="flex-1">
            {/* Profile Settings */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User size={20} />
                      Informações Pessoais
                    </CardTitle>
                    <CardDescription>
                      Atualize seus dados pessoais
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Avatar Upload */}
                      <div className="flex items-center gap-6 pb-6 border-b border-gray-200">
                        <div className="relative">
                          <div className="w-24 h-24 rounded-full bg-emerald-100 flex items-center justify-center overflow-hidden border-4 border-emerald-200">
                            {avatarLoading ? (
                              <CircleNotch size={32} className="animate-spin text-emerald-600" />
                            ) : avatarUrl ? (
                              <img
                                src={avatarUrl}
                                alt="Avatar"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-2xl font-semibold text-emerald-600">
                                {getInitials(profileData.name || 'U')}
                              </span>
                            )}
                          </div>
                          {/* Camera button overlay */}
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={avatarLoading}
                            className="absolute bottom-0 right-0 w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white hover:bg-emerald-700 transition-colors shadow-lg"
                            title="Alterar foto"
                          >
                            <Camera size={16} weight="bold" />
                          </button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            onChange={handleAvatarUpload}
                            className="hidden"
                          />
                        </div>

                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">Foto do Perfil</h3>
                          <p className="text-sm text-gray-500 mt-1">
                            Formatos aceitos: JPEG, PNG, GIF ou WebP. Tamanho máximo: 2MB.
                          </p>

                          {avatarError && (
                            <p className="text-sm text-red-600 mt-2">{avatarError}</p>
                          )}

                          <div className="flex gap-2 mt-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={avatarLoading}
                            >
                              <Camera size={16} className="mr-2" />
                              {avatarUrl ? 'Alterar foto' : 'Adicionar foto'}
                            </Button>
                            {avatarUrl && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleAvatarDelete}
                                disabled={avatarLoading}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash size={16} className="mr-2" />
                                Remover
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Form Fields */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nome Completo
                          </label>
                          <input
                            type="text"
                            value={profileData.name}
                            onChange={(e) => {
                              setProfileData({ ...profileData, name: e.target.value })
                              setHasChanges(true)
                            }}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email
                          </label>
                          <input
                            type="email"
                            value={profileData.email}
                            disabled
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Telefone
                          </label>
                          <input
                            type="tel"
                            value={profileData.phone}
                            onChange={(e) => {
                              setProfileData({ ...profileData, phone: e.target.value })
                              setHasChanges(true)
                            }}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Cargo
                          </label>
                          <input
                            type="text"
                            value={profileData.cargo}
                            onChange={(e) => {
                              setProfileData({ ...profileData, cargo: e.target.value })
                              setHasChanges(true)
                            }}
                            placeholder="Ex: Gerente de Operações"
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Departamento
                          </label>
                          <select
                            value={profileData.department}
                            onChange={(e) => {
                              setProfileData({ ...profileData, department: e.target.value })
                              setHasChanges(true)
                            }}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                          >
                            <option value="">Selecione...</option>
                            <option value="comercial">Comercial</option>
                            <option value="pricing">Pricing</option>
                            <option value="operacional">Operacional</option>
                            <option value="documentacao">Documentação</option>
                            <option value="financeiro">Financeiro</option>
                          </select>
                        </div>
                      </div>

                      <div className="pt-4 flex justify-end">
                        <Button
                          onClick={handleSaveProfile}
                          style={{ backgroundColor: 'rgb(5, 150, 105)' }}
                          disabled={!hasChanges || isSavingProfile}
                        >
                          {isSavingProfile ? (
                            <>
                              <CircleNotch size={16} className="animate-spin mr-2" />
                              Salvando...
                            </>
                          ) : (
                            'Salvar Alterações'
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Company Settings */}
            {activeTab === 'company' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Buildings size={20} />
                      Informações da Empresa
                    </CardTitle>
                    <CardDescription>
                      Dados cadastrais e informações de contato
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Company Logo Section */}
                      <div className="flex items-center gap-6 pb-6 border-b border-gray-200">
                        <div className="relative">
                          <div className="w-24 h-24 rounded-lg bg-emerald-100 flex items-center justify-center overflow-hidden border-4 border-emerald-200">
                            {logoLoading ? (
                              <CircleNotch size={32} className="animate-spin text-emerald-600" />
                            ) : logoUrl ? (
                              <img
                                src={logoUrl}
                                alt="Logo da empresa"
                                className="w-full h-full object-contain"
                              />
                            ) : (
                              <Buildings size={40} className="text-emerald-600" />
                            )}
                          </div>
                          {/* Camera button overlay */}
                          <button
                            onClick={() => logoInputRef.current?.click()}
                            disabled={logoLoading}
                            className="absolute bottom-0 right-0 w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white hover:bg-emerald-700 transition-colors shadow-lg"
                            title="Alterar logo"
                          >
                            <Camera size={16} weight="bold" />
                          </button>
                          <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                            onChange={handleLogoUpload}
                            className="hidden"
                          />
                        </div>

                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">Logo da Empresa</h3>
                          <p className="text-sm text-gray-500 mt-1">
                            Formatos aceitos: JPEG, PNG, GIF, WebP ou SVG. Tamanho máximo: 5MB.
                          </p>

                          {logoError && (
                            <p className="text-sm text-red-600 mt-2">{logoError}</p>
                          )}

                          <div className="flex gap-2 mt-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => logoInputRef.current?.click()}
                              disabled={logoLoading}
                            >
                              <Camera size={16} className="mr-2" />
                              {logoUrl ? 'Alterar logo' : 'Adicionar logo'}
                            </Button>
                            {logoUrl && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleLogoDelete}
                                disabled={logoLoading}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash size={16} className="mr-2" />
                                Remover
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Razão Social
                          </label>
                          <input
                            type="text"
                            value={companyData.name}
                            onChange={(e) => {
                              setCompanyData({ ...companyData, name: e.target.value })
                              setHasChanges(true)
                            }}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            CNPJ
                          </label>
                          <input
                            type="text"
                            value={companyData.document}
                            disabled
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Telefone
                          </label>
                          <input
                            type="tel"
                            value={companyData.phone}
                            onChange={(e) => {
                              setCompanyData({ ...companyData, phone: e.target.value })
                              setHasChanges(true)
                            }}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email
                          </label>
                          <input
                            type="email"
                            value={companyData.email}
                            onChange={(e) => {
                              setCompanyData({ ...companyData, email: e.target.value })
                              setHasChanges(true)
                            }}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Website
                          </label>
                          <input
                            type="url"
                            value={companyData.website}
                            onChange={(e) => {
                              setCompanyData({ ...companyData, website: e.target.value })
                              setHasChanges(true)
                            }}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </div>

                      {/* TODO: Implement address form */}
                      <div className="pt-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Endereço</h4>
                        <p className="text-sm text-gray-500">
                          {companyData.address.street}, {companyData.address.neighborhood} - {companyData.address.city}/{companyData.address.state}
                        </p>
                        <Button variant="outline" size="sm" className="mt-2">
                          Editar Endereço
                        </Button>
                      </div>

                      <div className="pt-4 flex justify-end">
                        <Button
                          onClick={handleSaveCompany}
                          style={{ backgroundColor: 'rgb(5, 150, 105)' }}
                          disabled={!hasChanges}
                        >
                          Salvar Alterações
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Exchange Rate Configuration */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CurrencyDollar size={20} />
                      Cotação do Dólar
                    </CardTitle>
                    <CardDescription>
                      Escolha a fonte de cotação do dólar para suas operações
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Rate Type Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Tipo de Cotação
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Comercial Option */}
                          <button
                            onClick={() => handleSaveExchangeRateType('comercial')}
                            disabled={exchangeRateLoading}
                            className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                              exchangeRateType === 'comercial'
                                ? 'border-emerald-500 bg-emerald-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            {exchangeRateType === 'comercial' && (
                              <div className="absolute top-2 right-2">
                                <Check size={20} className="text-emerald-600" />
                              </div>
                            )}
                            <h4 className="font-medium text-gray-900 mb-1">Dólar Comercial</h4>
                            <p className="text-sm text-gray-500">
                              Cotação em tempo real do mercado financeiro
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                              Atualização: A cada 5 minutos
                            </p>
                          </button>

                          {/* PTAX Option */}
                          <button
                            onClick={() => handleSaveExchangeRateType('ptax')}
                            disabled={exchangeRateLoading}
                            className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                              exchangeRateType === 'ptax'
                                ? 'border-emerald-500 bg-emerald-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            {exchangeRateType === 'ptax' && (
                              <div className="absolute top-2 right-2">
                                <Check size={20} className="text-emerald-600" />
                              </div>
                            )}
                            <h4 className="font-medium text-gray-900 mb-1">Dólar PTAX</h4>
                            <p className="text-sm text-gray-500">
                              Taxa oficial do Banco Central do Brasil
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                              Atualização: ~13h em dias úteis
                            </p>
                          </button>
                        </div>
                      </div>

                      {/* Current Rate Display */}
                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-gray-700">
                            Cotação Atual ({exchangeRateType === 'ptax' ? 'PTAX' : 'Comercial'})
                          </h4>
                          <button
                            onClick={fetchCurrentRate}
                            disabled={rateLoading}
                            className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                          >
                            <ArrowsClockwise size={16} className={rateLoading ? 'animate-spin' : ''} />
                            Atualizar
                          </button>
                        </div>

                        {rateLoading ? (
                          <div className="flex items-center gap-2 text-gray-500">
                            <CircleNotch size={16} className="animate-spin" />
                            Carregando cotação...
                          </div>
                        ) : currentRate ? (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <span className="text-xs text-gray-500 block">Compra</span>
                                <span className="text-lg font-semibold text-gray-900">
                                  R$ {currentRate.buy.toFixed(4)}
                                </span>
                              </div>
                              <div>
                                <span className="text-xs text-gray-500 block">Venda</span>
                                <span className="text-lg font-semibold text-gray-900">
                                  R$ {currentRate.sell.toFixed(4)}
                                </span>
                              </div>
                            </div>
                            <p className="text-xs text-gray-400 mt-3">
                              Data da cotação: {currentRate.date}
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">
                            Não foi possível carregar a cotação
                          </p>
                        )}
                      </div>

                      {/* Spread fixo do agente (Plane #25) — aplicado por agente, não por oferta */}
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-900 mb-1">Spread (%)</h4>
                        <p className="text-xs text-gray-500 mb-3">
                          Margem fixa deste agente, exibida aos embarcadores em "Meus Agentes".
                        </p>
                        <div className="flex items-center gap-2 max-w-xs">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="Ex: 5"
                            value={spread}
                            onChange={(e) => setSpread(e.target.value)}
                          />
                          <Button onClick={handleSaveSpread} disabled={savingSpread}>
                            {savingSpread ? 'Salvando...' : 'Salvar'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Currency Preference */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CurrencyDollar size={20} />
                      Moeda Padrão
                    </CardTitle>
                    <CardDescription>
                      Defina a moeda de preferência para entrada de valores nas ofertas
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Moeda para Entrada de Valores
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* BRL Option */}
                          <button
                            onClick={() => handleSavePreferredCurrency('BRL')}
                            disabled={currencyLoading}
                            className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                              preferredCurrency === 'BRL'
                                ? 'border-emerald-500 bg-emerald-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            {preferredCurrency === 'BRL' && (
                              <div className="absolute top-2 right-2">
                                <Check size={20} className="text-emerald-600" />
                              </div>
                            )}
                            <h4 className="font-medium text-gray-900 mb-1">Real (R$)</h4>
                            <p className="text-sm text-gray-500">
                              Valores em Reais com conversão para Dólar
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                              Ideal para empresas brasileiras
                            </p>
                          </button>

                          {/* USD Option */}
                          <button
                            onClick={() => handleSavePreferredCurrency('USD')}
                            disabled={currencyLoading}
                            className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                              preferredCurrency === 'USD'
                                ? 'border-emerald-500 bg-emerald-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            {preferredCurrency === 'USD' && (
                              <div className="absolute top-2 right-2">
                                <Check size={20} className="text-emerald-600" />
                              </div>
                            )}
                            <h4 className="font-medium text-gray-900 mb-1">Dólar (US$)</h4>
                            <p className="text-sm text-gray-500">
                              Valores em Dólar com conversão para Real
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                              Padrão internacional de frete
                            </p>
                          </button>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-gray-200">
                        <p className="text-sm text-gray-600">
                          <strong>Como funciona:</strong> Ao criar ofertas, você digitará os valores na moeda selecionada.
                          O sistema mostrará automaticamente a conversão para a outra moeda usando a cotação configurada acima.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Team Tab */}
            {activeTab === 'team' && <TeamTab />}

            {/* Notification Settings */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Bell size={20} />
                      Preferências de Notificação
                    </CardTitle>
                    <CardDescription>
                      Configure como deseja receber notificações
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {/* Email Notifications */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <EnvelopeSimple size={16} />
                          Notificações por Email
                        </h4>
                        <div className="space-y-3">
                          {Object.entries(notificationSettings.emailNotifications).map(([key, value]) => (
                            <label key={key} className="flex items-center justify-between py-2 cursor-pointer">
                              <span className="text-sm text-gray-700">
                                {key === 'newOpportunities' && 'Novas oportunidades de cotação'}
                                {key === 'proposalUpdates' && 'Atualizações de propostas'}
                                {key === 'shipmentAlerts' && 'Alertas de embarques'}
                                {key === 'clientMessages' && 'Mensagens de clientes'}
                                {key === 'weeklyReport' && 'Relatório semanal de performance'}
                              </span>
                              <input
                                type="checkbox"
                                checked={value}
                                onChange={(e) => {
                                  setNotificationSettings({
                                    ...notificationSettings,
                                    emailNotifications: {
                                      ...notificationSettings.emailNotifications,
                                      [key]: e.target.checked,
                                    },
                                  })
                                  setHasChanges(true)
                                }}
                                className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                              />
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Push Notifications */}
                      <div className="pt-4 border-t border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <Bell size={16} />
                          Notificações Push
                        </h4>
                        <div className="space-y-3">
                          {Object.entries(notificationSettings.pushNotifications).map(([key, value]) => (
                            <label key={key} className="flex items-center justify-between py-2 cursor-pointer">
                              <span className="text-sm text-gray-700">
                                {key === 'urgentOpportunities' && 'Oportunidades urgentes'}
                                {key === 'acceptedProposals' && 'Propostas aceitas'}
                                {key === 'shipmentMilestones' && 'Marcos importantes de embarques'}
                              </span>
                              <input
                                type="checkbox"
                                checked={value}
                                onChange={(e) => {
                                  setNotificationSettings({
                                    ...notificationSettings,
                                    pushNotifications: {
                                      ...notificationSettings.pushNotifications,
                                      [key]: e.target.checked,
                                    },
                                  })
                                  setHasChanges(true)
                                }}
                                className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                              />
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="pt-4 flex justify-end">
                        <Button
                          onClick={handleSaveNotifications}
                          style={{ backgroundColor: 'rgb(5, 150, 105)' }}
                          disabled={!hasChanges}
                        >
                          Salvar Alterações
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Security Settings */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ShieldCheck size={20} />
                      Segurança da Conta
                    </CardTitle>
                    <CardDescription>
                      Gerencie a segurança da sua conta
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {/* Password Change */}
                      <div className="pb-4 border-b border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-900 mb-4">Alterar Senha</h4>

                        {passwordError && (
                          <div className="p-3 mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                            {passwordError}
                          </div>
                        )}

                        {passwordSuccess && (
                          <div className="p-3 mb-4 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                            {passwordSuccess}
                          </div>
                        )}

                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Senha Atual
                            </label>
                            <input
                              type="password"
                              value={currentPassword}
                              onChange={(e) => setCurrentPassword(e.target.value)}
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                              placeholder="Digite sua senha atual"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Nova Senha
                            </label>
                            <input
                              type="password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                              placeholder="Mínimo 6 caracteres"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Confirmar Nova Senha
                            </label>
                            <input
                              type="password"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                              placeholder="Digite a nova senha novamente"
                            />
                          </div>
                          <div className="flex justify-end">
                            <Button
                              onClick={handleChangePassword}
                              disabled={passwordLoading}
                              style={{ backgroundColor: 'rgb(5, 150, 105)' }}
                            >
                              {passwordLoading ? (
                                <>
                                  <CircleNotch size={16} className="animate-spin mr-2" />
                                  Atualizando...
                                </>
                              ) : (
                                'Atualizar Senha'
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Two-Factor Authentication */}
                      <div className="flex items-center justify-between py-4 border-b border-gray-200">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">
                            Autenticação em Dois Fatores
                          </h4>
                          <p className="text-sm text-gray-500 mt-1">
                            {securitySettings.twoFactorEnabled
                              ? 'Ativada - Segurança adicional para sua conta'
                              : 'Adicione uma camada extra de segurança'}
                          </p>
                        </div>
                        {securitySettings.twoFactorEnabled ? (
                          <span className="inline-flex items-center gap-2 text-emerald-700 bg-emerald-100 px-3 py-1 rounded-full text-sm font-medium">
                            <Check size={16} />
                            Ativada
                          </span>
                        ) : (
                          <Button
                            variant="outline"
                            onClick={handleEnable2FA}
                          >
                            Ativar 2FA
                          </Button>
                        )}
                      </div>

                      {/* Login Alerts */}
                      <div className="flex items-center justify-between py-4 border-b border-gray-200">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">
                            Alertas de Login
                          </h4>
                          <p className="text-sm text-gray-500 mt-1">
                            Receba notificações sobre logins em novos dispositivos
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={securitySettings.loginAlerts}
                          onChange={(e) => {
                            setSecuritySettings({
                              ...securitySettings,
                              loginAlerts: e.target.checked,
                            })
                            setHasChanges(true)
                          }}
                          className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      {/* Session Timeout */}
                      <div className="py-4">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">
                          Tempo de Sessão
                        </h4>
                        <p className="text-sm text-gray-500 mb-3">
                          Tempo de inatividade antes de exigir novo login
                        </p>
                        <select
                          value={securitySettings.sessionTimeout}
                          onChange={(e) => {
                            setSecuritySettings({
                              ...securitySettings,
                              sessionTimeout: e.target.value,
                            })
                            setHasChanges(true)
                          }}
                          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="15">15 minutos</option>
                          <option value="30">30 minutos</option>
                          <option value="60">1 hora</option>
                          <option value="120">2 horas</option>
                        </select>
                      </div>

                      <div className="pt-4 flex justify-end">
                        <Button
                          onClick={handleSaveSecurity}
                          style={{ backgroundColor: 'rgb(5, 150, 105)' }}
                          disabled={!hasChanges}
                        >
                          Salvar Alterações
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Billing Settings */}
            {activeTab === 'billing' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard size={20} />
                      Assinatura & Faturamento
                    </CardTitle>
                    <CardDescription>
                      Gerencie sua assinatura e métodos de pagamento
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-12">
                      <CreditCard size={64} className="mx-auto text-gray-400 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Faturamento em Desenvolvimento
                      </h3>
                      <p className="text-gray-500 mb-4">
                        Sistema de assinatura e pagamentos será implementado em breve.
                      </p>
                      <p className="text-sm text-gray-400">
                        TODO: Implementar integração com gateway de pagamento (Stripe, PagSeguro, etc.)
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Templates Settings */}
            {activeTab === 'templates' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Lightning size={20} className="text-amber-500" />
                      Templates de Ofertas
                    </CardTitle>
                    <CardDescription>
                      Gerencie seus templates de taxas para agilizar a criação de ofertas
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {allTemplates.length === 0 ? (
                      <div className="text-center py-12">
                        <Lightning size={64} className="mx-auto text-gray-300 mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          Nenhum template salvo
                        </h3>
                        <p className="text-gray-500 mb-4">
                          Crie templates ao enviar ofertas para agilizar seu trabalho.
                        </p>
                        <p className="text-sm text-gray-400">
                          Ao criar uma oferta, use o painel "Templates Salvos" para salvar suas configurações.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Lista de templates agrupados por modalidade */}
                        {['maritimo', 'aereo', 'terrestre'].map(modality => {
                          const modalityTemplates = allTemplates.filter(t => t.modality === modality)
                          if (modalityTemplates.length === 0) return null

                          return (
                            <div key={modality} className="border border-gray-200 rounded-lg">
                              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                                <h4 className="font-medium text-gray-900">
                                  {modality === 'maritimo' ? 'Marítimo' :
                                   modality === 'aereo' ? 'Aéreo' : 'Terrestre'}
                                </h4>
                              </div>
                              <div className="divide-y divide-gray-100">
                                {modalityTemplates.map(template => (
                                  <div key={template.id} className="p-4 hover:bg-gray-50">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <p className="font-medium text-gray-900">{template.name}</p>
                                        <p className="text-sm text-gray-500">
                                          {template.breakdown.length} taxas • Criado em {new Date(template.createdAt).toLocaleDateString('pt-BR')}
                                        </p>
                                        <div className="flex flex-wrap gap-1 mt-2">
                                          {template.breakdown.slice(0, 4).map((item, idx) => (
                                            <span
                                              key={idx}
                                              className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600"
                                            >
                                              {item.name}
                                            </span>
                                          ))}
                                          {template.breakdown.length > 4 && (
                                            <span className="text-xs text-gray-400">
                                              +{template.breakdown.length - 4} mais
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            setEditingTemplate(template)
                                            setIsEditModalOpen(true)
                                          }}
                                          className="gap-1"
                                        >
                                          <PencilSimple size={14} />
                                          Editar
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            if (window.confirm('Tem certeza que deseja excluir este template?')) {
                                              deleteTemplate(template.id)
                                              refreshTemplates()
                                            }
                                          }}
                                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        >
                                          <Trash size={14} />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Edit Template Modal */}
                {editingTemplate && (
                  <EditTemplateModal
                    template={editingTemplate}
                    isOpen={isEditModalOpen}
                    onClose={() => {
                      setIsEditModalOpen(false)
                      setEditingTemplate(null)
                    }}
                    onSave={handleTemplateUpdate}
                  />
                )}
              </div>
            )}

            {/* Conditions Settings */}
            {activeTab === 'conditions' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText size={20} className="text-blue-500" />
                      Condições de Ofertas
                    </CardTitle>
                    <CardDescription>
                      Crie e gerencie condições padrão para incluir em suas ofertas.
                      Condições marcadas como padrão serão pré-selecionadas automaticamente.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Add New Condition Form */}
                    <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <h4 className="text-sm font-semibold text-gray-900 mb-4">
                        {editingCondition ? 'Editar Condição' : 'Adicionar Nova Condição'}
                      </h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Título da Condição
                          </label>
                          <input
                            type="text"
                            value={editingCondition ? editingCondition.title : newCondition.title}
                            onChange={(e) => {
                              if (editingCondition) {
                                setEditingCondition({ ...editingCondition, title: e.target.value })
                              } else {
                                setNewCondition({ ...newCondition, title: e.target.value })
                              }
                            }}
                            placeholder="Ex: Termos de Pagamento, Seguro, etc."
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Conteúdo da Condição
                          </label>
                          <textarea
                            value={editingCondition ? editingCondition.content : newCondition.content}
                            onChange={(e) => {
                              if (editingCondition) {
                                setEditingCondition({ ...editingCondition, content: e.target.value })
                              } else {
                                setNewCondition({ ...newCondition, content: e.target.value })
                              }
                            }}
                            placeholder="Descreva a condição em detalhes..."
                            rows={5}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editingCondition ? editingCondition.isDefault : newCondition.isDefault}
                              onChange={(e) => {
                                if (editingCondition) {
                                  setEditingCondition({ ...editingCondition, isDefault: e.target.checked })
                                } else {
                                  setNewCondition({ ...newCondition, isDefault: e.target.checked })
                                }
                              }}
                              className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                            />
                            <span className="text-sm text-gray-700">
                              Marcar como padrão (pré-selecionar em novas ofertas)
                            </span>
                          </label>
                          <div className="flex items-center gap-2">
                            {editingCondition && (
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setEditingCondition(null)
                                  setNewCondition({ title: '', content: '', isDefault: false })
                                }}
                              >
                                Cancelar
                              </Button>
                            )}
                            <Button
                              onClick={() => {
                                if (editingCondition) {
                                  updateCondition(
                                    editingCondition.id,
                                    editingCondition.title,
                                    editingCondition.content,
                                    editingCondition.isDefault
                                  )
                                  setEditingCondition(null)
                                  refreshConditions()
                                } else if (newCondition.title.trim() && newCondition.content.trim()) {
                                  saveCondition(newCondition.title, newCondition.content, newCondition.isDefault)
                                  setNewCondition({ title: '', content: '', isDefault: false })
                                  refreshConditions()
                                }
                              }}
                              disabled={
                                editingCondition
                                  ? !editingCondition.title.trim() || !editingCondition.content.trim()
                                  : !newCondition.title.trim() || !newCondition.content.trim()
                              }
                              style={{ backgroundColor: 'rgb(5, 150, 105)' }}
                            >
                              {editingCondition ? 'Salvar Alterações' : 'Adicionar Condição'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Conditions List */}
                    {allConditions.length === 0 ? (
                      <div className="text-center py-12">
                        <FileText size={64} className="mx-auto text-gray-300 mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          Nenhuma condição cadastrada
                        </h3>
                        <p className="text-gray-500 mb-4">
                          Adicione condições padrão para agilizar a criação de ofertas.
                        </p>
                        <p className="text-sm text-gray-400">
                          Condições podem incluir termos de pagamento, prazos, garantias, etc.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-gray-900">
                          Condições Cadastradas ({allConditions.length})
                        </h4>
                        {allConditions.map((condition) => (
                          <div
                            key={condition.id}
                            className={`p-4 border rounded-lg ${
                              condition.isDefault
                                ? 'border-emerald-200 bg-emerald-50'
                                : 'border-gray-200 bg-white'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h5 className="font-medium text-gray-900">{condition.title}</h5>
                                  {condition.isDefault && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                                      <Check size={12} className="mr-1" />
                                      Padrão
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 whitespace-pre-wrap line-clamp-3">
                                  {condition.content}
                                </p>
                                <p className="text-xs text-gray-400 mt-2">
                                  Criado em {new Date(condition.createdAt).toLocaleDateString('pt-BR')}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 ml-4">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => toggleDefault(condition.id)}
                                  title={condition.isDefault ? 'Remover do padrão' : 'Definir como padrão'}
                                >
                                  {condition.isDefault ? (
                                    <Check size={14} className="text-emerald-600" />
                                  ) : (
                                    <Check size={14} className="text-gray-400" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEditingCondition(condition)}
                                  className="gap-1"
                                >
                                  <PencilSimple size={14} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    if (window.confirm('Tem certeza que deseja excluir esta condição?')) {
                                      deleteCondition(condition.id)
                                      refreshConditions()
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash size={14} />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </main>
    </AppLayout>
  )
}
